/**
 * Helpers for treating an application as a single repository (1:1), while still
 * rendering legacy applications that contain multiple repositories.
 *
 * An application created in the repository-centric flow has exactly one entry in
 * `repositories`. Older applications may have several; those are "legacy multi-repo"
 * and must keep working, so these helpers branch on the repository count rather than
 * assuming a single repo.
 */
import { Application, ApplicationRepository } from '../types/application';
import { DEFAULT_PROVIDER, GitProvider, normalizeProvider } from '../config/providers';

/** The shape these helpers need — satisfied by Application and ApplicationResponse. */
type RepoBearing = Pick<Application, 'name' | 'repositories'>;

/** The primary (or only) repository of an application, if any. */
export function getPrimaryRepository(
  app: Pick<Application, 'repositories'>
): ApplicationRepository | undefined {
  return app.repositories?.[0];
}

/** True when an application still holds more than one repository (legacy). */
export function isLegacyMultiRepo(app: Pick<Application, 'repositories'>): boolean {
  return (app.repositories?.length ?? 0) > 1;
}

/**
 * The repository name without its owner/namespace prefix.
 * Handles nested GitLab namespaces (e.g. "group/sub/repo" -> "sub/repo").
 */
export function getRepositoryShortName(fullName: string): string {
  return fullName.split('/').slice(1).join('/') || fullName;
}

/**
 * Title to show for an application: the repository `full_name` when it maps 1:1 to a
 * single repository, otherwise the application's own name (legacy multi-repo / empty).
 */
export function getApplicationDisplayTitle(app: RepoBearing): string {
  if ((app.repositories?.length ?? 0) === 1) {
    return app.repositories[0].full_name;
  }
  return app.name;
}

/** Pluralized "N repository/repositories" label. */
export function getRepositoryCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'repository' : 'repositories'}`;
}

/**
 * Unique key for a repository that includes its provider, so the same `full_name` on
 * two providers never collides. Missing providers are legacy GitHub repositories.
 */
export function getRepositoryKey(provider: string | null | undefined, fullName: string): string {
  return `${normalizeProvider(provider)}:${fullName}`;
}

/** Inverse of `getRepositoryKey`. A key without a provider prefix is treated as GitHub. */
export function parseRepositoryKey(repoKey: string): { provider: GitProvider; fullName: string } {
  const colonIndex = repoKey.indexOf(':');
  if (colonIndex === -1) return { provider: DEFAULT_PROVIDER, fullName: repoKey };
  return {
    provider: normalizeProvider(repoKey.substring(0, colonIndex)),
    fullName: repoKey.substring(colonIndex + 1),
  };
}
